// ExtensionPointRegistry (workspace-modes note §10, §11.2): the named surfaces where features,
// and later apps, plug into the shell. Slice 1 accepts first-party registrations only: no
// manifest, no iframe host, no app principal. The ModeRegistry is the `mode` surface, read by
// the rail, the sidebar, the router guard, useFeature and ⌘K.
import type { LucideIcon } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import type { CompanyRole, FeatureKey } from "../api/types";
import type { CompanyAccess } from "../features/company";
import type { FeatureState } from "../features/use-feature";
import type { Focus } from "../focus/focus";

export const SURFACES = ["mode", "panel", "card", "seat_widget", "dock_item"] as const;
export type Surface = (typeof SURFACES)[number];

/** Only CompanyOS itself registers in slice 1; `company` and third parties come after M4. */
export type Publisher = "companyos";

type BaseEntry = {
  id: string;
  publisher: Publisher;
  /** Add-on that must be enabled for the entry to exist (`core` = always). */
  feature: FeatureKey;
};

/** Route a mode lives at; one per registered mode (kept literal for typed links). */
export type ModePath =
  | "/c/$slug/desk"
  | "/c/$slug/chat"
  | "/c/$slug/work"
  | "/c/$slug/kpis"
  | "/c/$slug/agents"
  | "/c/$slug/kpis/reports"
  | "/c/$slug/calendar"
  | "/c/$slug/live"
  | "/c/$slug/setup"
  | "/c/$slug/portfolio";

export type ModeEntry = BaseEntry & {
  key: string;
  label: string;
  icon: LucideIcon;
  path: ModePath;
  /** Feature keys of overlays the mode offers (e.g. Chart's kpi/finance overlays). */
  overlayFeatures: FeatureKey[];
  /** Roles that may open the mode. */
  roles: CompanyRole[];
  /**
   * Roles that see the mode but reach only its routes marked open (`ModeGate open`; MEMBER-ENTRY:
   * a plain member's Cadence is its own scorecard and numbers).
   */
  openRoles?: CompanyRole[];
  /** Roles that land on this mode when opening the company. */
  defaultFor: CompanyRole[];
  /** Second key of the `g` chord (`g k` → KPIs). */
  shortcut: string;
  /** The single key that switches to the mode outside editors (`1`–`5`, tabs only). */
  digit?: string;
  /** Position in the mobile bottom bar and its "More" menu (lower first). */
  mobileOrder: number;
  /**
   * Where the mode sits (NAV-REDESIGN, founder-approved 2026-10-29): `global` at the rail's top
   * (Home, Live), `tab` in the mode bar centred in the top bar (Chat, Work, Calendar, KPIs),
   * `foot` at the rail's foot above Me (Workforce, Settings), `section` inside another mode with
   * no tab or rail item of its own (Reports, in KPIs).
   */
  placement: "global" | "tab" | "foot" | "section";
  /** The rail's and the phone bar's name when it differs from `label` (Setup reads "Settings"). */
  railLabel?: string;
  /** The rail item's tooltip when it says more than the name (Desk: what is on it). */
  railHint?: string;
  /** The mode's own sidebar panel (design spec 2026-10-19 §4); none for a mode being built. */
  sidebar?: ComponentType<SidebarProps>;
  /** The mode is a landing page (Home, from Phase 4). */
  landing?: boolean;
  /**
   * FOUNDER-OS: the company kinds the mode exists in (absent: every kind). A kind-specific mode
   * is that company's landing for the roles in `defaultFor` (Portfolio in Founder OS).
   */
  companyKinds?: CompanyAccess["kind"][];
};

/** What the shell hands a mode's sidebar: the company in view (data comes from hooks). */
export type SidebarProps = {
  company: CompanyAccess;
  /** Shell-owned rows drawn at the end of the scroll area (the pinned apps until Phase 4). */
  children?: ReactNode;
};

/**
 * What the shell hands a side panel when it mounts it (the mounting contract). A panel reads
 * the shared playhead and its own entitlement; it never fetches outside the company in view,
 * and it renders nothing outside its tab.
 */
export type PanelProps = {
  focus: Focus;
  company: CompanyAccess;
  /** The panel's own feature state (enabled is always true when mounted; readOnly may be). */
  feature: FeatureState;
  /** The mode the panel is shown beside. */
  modeKey: string;
};

export type PanelEntry = BaseEntry & {
  key: string;
  label: string;
  /** Modes the panel appears beside. */
  modes: readonly string[] | "all";
  /** Tab order (lower first). */
  order: number;
  /** The tab's label beside a given mode, when it differs there (CHAT-PANEL). */
  labelIn?: Readonly<Record<string, string>>;
  /** The panel scrolls inside itself and fills the body edge to edge (a thread). */
  flush?: boolean;
  component: ComponentType<PanelProps>;
};
export type CardEntry = BaseEntry & { type: string };
export type SeatWidgetEntry = BaseEntry & { key: string; label: string };
export type DockItemEntry = BaseEntry & { key: string; label: string; icon: LucideIcon };

export type EntryOf = {
  mode: ModeEntry;
  panel: PanelEntry;
  card: CardEntry;
  seat_widget: SeatWidgetEntry;
  dock_item: DockItemEntry;
};

export class ExtensionPointRegistry {
  private readonly entries = new Map<Surface, Map<string, EntryOf[Surface]>>(
    SURFACES.map((s) => [s, new Map()]),
  );

  register<S extends Surface>(surface: S, entry: EntryOf[S]): this {
    if (entry.publisher !== "companyos") {
      throw new Error(`only first-party entries can register in slice 1 (got ${entry.publisher})`);
    }
    const bucket = this.entries.get(surface);
    if (!bucket) throw new Error(`unknown surface ${surface}`);
    if (bucket.has(entry.id)) throw new Error(`${surface} ${entry.id} is already registered`);
    bucket.set(entry.id, entry);
    return this;
  }

  list<S extends Surface>(surface: S): EntryOf[S][] {
    return [...(this.entries.get(surface)?.values() ?? [])] as EntryOf[S][];
  }

  get<S extends Surface>(surface: S, id: string): EntryOf[S] | undefined {
    return this.entries.get(surface)?.get(id) as EntryOf[S] | undefined;
  }
}
